import ProductService from "@/services/productService";

import { create } from "zustand";

const useProductsStore = create((set, get) => ({
  loading: false,
  error: null,
  products: [],
  meta: null,
  filters: {
    page: 1,
    size: localStorage.getItem("pageSize") || 10,
    totalPages: 0,
    totalElements: 0,
    // Search query
    query: "",
    // Category filters
    mainCategoryId: "",
    categoryId: "",
    subCategoryId: "",
    subCategoryBrandId: "",
    // Source filter
    source: "",
    // Price range
    priceMin: "",
    priceMax: "",
    // Date range
    createdAtStart: "",
    createdAtEnd: "",
  },
  // Sort state
  sort: [],

  getProducts: async () => {
    set({ loading: true, error: null });
    const state = get();

    // Build filter array for advanced API
    const filterArray = [];

    // Name search
    if (state.filters.query) {
      filterArray.push({ field: "name", operator: "contains", value: state.filters.query });
    }

    // Category filters
    if (state.filters.mainCategoryId) {
      filterArray.push({ field: "mainCategoryId", operator: "eq", value: state.filters.mainCategoryId });
    }
    if (state.filters.categoryId) {
      filterArray.push({ field: "categoryId", operator: "eq", value: state.filters.categoryId });
    }
    if (state.filters.subCategoryId) {
      filterArray.push({ field: "subCategoryId", operator: "eq", value: state.filters.subCategoryId });
    }
    if (state.filters.subCategoryBrandId) {
      filterArray.push({ field: "subCategoryBrandId", operator: "eq", value: state.filters.subCategoryBrandId });
    }

    // Source filter
    if (state.filters.source) {
      filterArray.push({ field: "source", operator: "eq", value: state.filters.source });
    }

    // Price range
    if (state.filters.priceMin && state.filters.priceMax) {
      filterArray.push({ field: "price", operator: "between", value: `${state.filters.priceMin}|${state.filters.priceMax}` });
    } else if (state.filters.priceMin) {
      filterArray.push({ field: "price", operator: "gte", value: state.filters.priceMin });
    } else if (state.filters.priceMax) {
      filterArray.push({ field: "price", operator: "lte", value: state.filters.priceMax });
    }

    // Date range - format: YYYY-MM-DD
    if (state.filters.createdAtStart && state.filters.createdAtEnd) {
      filterArray.push({ field: "createdAt", operator: "between", value: `${state.filters.createdAtStart}|${state.filters.createdAtEnd}` });
    } else if (state.filters.createdAtStart) {
      filterArray.push({ field: "createdAt", operator: "gte", value: state.filters.createdAtStart });
    } else if (state.filters.createdAtEnd) {
      filterArray.push({ field: "createdAt", operator: "lte", value: state.filters.createdAtEnd });
    }

    try {
      const data = await ProductService.getProductsAdvanced({
        page: state.filters.page,
        size: state.filters.size,
        filter: filterArray,
        sort: state.sort,
      });

      set({
        products: data.content,
        loading: false,
        meta: data.meta || null,
        filters: {
          ...state.filters,
          totalPages: data.totalPages,
          totalElements: data.totalElements,
          page: data.page,
        },
      });
    } catch (error) {
      // Fallback to old API if advanced endpoint fails
      console.warn("Advanced API failed, falling back to legacy API:", error);
      try {
        const data = await ProductService.getProducts(state.filters);
        set({
          products: data.content,
          loading: false,
          filters: {
            ...state.filters,
            totalPages: data.totalPages,
            page: data.page,
          },
        });
      } catch (fallbackError) {
        const message =
          fallbackError?.response?.data?.errors?.[0]?.message ||
          fallbackError?.message ||
          "Ürünler yüklenemedi";
        set({ loading: false, error: message, products: [] });
      }
    }
  },

  setFilters: (filters) => {
    set({ filters });
  },

  // EPS-4850 B-4 fix: atomic set + fetch. Caller'ların `setFilters(newF); getProducts()`
  // pattern'i Zustand action wrapper'ında re-render sırasını garantilemiyordu;
  // bazı durumlarda getProducts eski filter'ı okuyup yanlış sayfa/filtre fetch
  // ediyordu. Bu action set() ile aynı kapanış içinde fetch'i tetikleyerek
  // race'i ortadan kaldırır.
  setFiltersAndFetch: (filters) => {
    set({ filters });
    return get().getProducts();
  },

  // Sort functions
  setSort: (sortArray) => {
    set({ sort: sortArray });
  },

  toggleSort: (field) => {
    const state = get();
    const existingSort = state.sort.find((s) => s.field === field);

    let newSort;
    if (!existingSort) {
      newSort = [{ field, direction: "ASC" }];
    } else if (existingSort.direction === "ASC") {
      newSort = [{ field, direction: "DESC" }];
    } else {
      newSort = [];
    }

    set({ sort: newSort });
    get().getProducts();
  },

  clearSort: () => {
    set({ sort: [] });
    get().getProducts();
  },

  clearAllFilters: () => {
    set({
      filters: {
        page: 1,
        size: localStorage.getItem("pageSize") || 10,
        totalPages: 0,
        totalElements: 0,
        query: "",
        mainCategoryId: "",
        categoryId: "",
        subCategoryId: "",
        subCategoryBrandId: "",
        source: "",
        priceMin: "",
        priceMax: "",
        createdAtStart: "",
        createdAtEnd: "",
      },
      sort: [],
    });
    get().getProducts();
  },
}));

export default useProductsStore;
